import { useState, useEffect, useCallback } from 'react'
import { Badge, Button, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { URL } from '../../../gql/client'
import { parseId } from '../../../helpers'

interface EZLynxStatus {
  synced: boolean
  external_id: string | null
  synced_at: string | null
}

interface Props {
  leadId: string  // Relay ID o numeric string
  showButton?: boolean
}

export const EZLynxSyncBadge: React.FC<Props> = ({ leadId, showButton = true }) => {
  const { auth } = useAuth()
  const [status, setStatus] = useState<EZLynxStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)

  const getToken = () => auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)

  // Convertir Relay ID a numeric si es necesario
  const numericId = (() => {
    try {
      // Si es un número directo, úsalo
      if (/^\d+$/.test(leadId)) return leadId
      // Si es Relay ID (base64), decodifícalo
      return String(parseId(leadId))
    } catch {
      return leadId
    }
  })()

  const fetchStatus = useCallback(async () => {
    const token = getToken()
    if (!token || !numericId) return

    setLoading(true)
    try {
      const res = await axios.get(`${URL}/api/ezlynx/leads/${numericId}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setStatus(res.data)
    } catch {
      // Silencioso — la integración puede no estar habilitada
    } finally {
      setLoading(false)
    }
  }, [numericId])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const handleSync = async () => {
    const token = getToken()
    if (!token) return

    setSyncing(true)
    try {
      const res = await axios.post(
        `${URL}/api/ezlynx/leads/${numericId}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (res.data.success) {
        toast.success(res.data.message)
        await fetchStatus()  // Refrescar badge
      } else {
        toast.error(res.data.error || 'Sync failed')
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Error syncing to EZLynx'
      toast.error(msg)
    } finally {
      setSyncing(false)
    }
  }

  if (loading) {
    return (
      <span className="d-inline-flex align-items-center gap-1 text-muted fs-8">
        <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} />
        <small>EZLynx</small>
      </span>
    )
  }

  if (!status) return null  // No está habilitado o hubo error silencioso

  return (
    <div className="d-inline-flex align-items-center gap-2">
      {status.synced ? (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              <div className="text-start">
                <strong>EZLynx Synced</strong>
                <br />
                Applicant ID: <code>{status.external_id}</code>
                {status.synced_at && (
                  <>
                    <br />
                    <small>{new Date(status.synced_at).toLocaleDateString()}</small>
                  </>
                )}
              </div>
            </Tooltip>
          }
        >
          <Badge
            bg="success"
            className="d-inline-flex align-items-center gap-1 cursor-pointer"
            style={{ fontSize: '0.7rem', padding: '3px 8px' }}
          >
            <i className="bi bi-check-circle-fill" style={{ fontSize: '0.65rem' }} />
            EZLynx
          </Badge>
        </OverlayTrigger>
      ) : (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>Not synced with EZLynx. Click "Sync" to push this lead.</Tooltip>}
        >
          <Badge
            bg="secondary"
            className="d-inline-flex align-items-center gap-1"
            style={{ fontSize: '0.7rem', padding: '3px 8px' }}
          >
            <i className="bi bi-cloud-slash" style={{ fontSize: '0.65rem' }} />
            EZLynx
          </Badge>
        </OverlayTrigger>
      )}

      {showButton && !status.synced && (
        <Button
          variant="light-primary"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          className="py-1 px-3"
          style={{ fontSize: '0.75rem' }}
        >
          {syncing ? (
            <><Spinner animation="border" size="sm" className="me-1" style={{ width: '10px', height: '10px' }} />Syncing...</>
          ) : (
            <><i className="bi bi-arrow-up-circle me-1" />Sync to EZLynx</>
          )}
        </Button>
      )}

      {showButton && status.synced && (
        <Button
          variant="light"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          className="py-1 px-3"
          style={{ fontSize: '0.75rem' }}
          title="Re-sync lead data"
        >
          {syncing ? (
            <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} />
          ) : (
            <i className="bi bi-arrow-repeat" />
          )}
        </Button>
      )}
    </div>
  )
}
